import React from "react"

import { format } from "date-fns"
import {
  AlertTriangle,
  Caravan,
  Clock,
  ExternalLink,
  Loader2,
  MapPin,
  Truck,
  User,
  UserCheck,
  Users,
} from "lucide-react"

import { getSamsaraRouteUrl } from "@/config/constants"
import { type SheriffBooking } from "@/lib/sheriffBooking"
import { COVER_BETA_NOTE } from "@/lib/shiftCover"
import { toneClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"
import { Route } from "@/types/collections"

import { AssignmentCombobox, type AssignmentPicker } from "../AssignmentCombobox"
import { SummaryCard, SummaryRow } from "../DetailsPanel"
import { type RouteAssignment } from "./useRouteAssignment"

interface RouteAssignmentSummaryProps {
  route: Route
  isRouteDateToday: boolean
  /**
   * Without it the vehicle is read-only and the trailer row is hidden; the driver stays editable.
   */
  canAssignVehicles: boolean
  assignment: RouteAssignment
  sheriffDriver: SheriffBooking | null
  /** Owned by the panel so a dismiss can close it first. */
  openPicker: AssignmentPicker | null
  onPickerOpenChange: (picker: AssignmentPicker) => (isOpen: boolean) => void
}

const RouteAssignmentSummary: React.FC<RouteAssignmentSummaryProps> = ({
  route,
  isRouteDateToday,
  canAssignVehicles,
  assignment,
  sheriffDriver,
  openPicker,
  onPickerOpenChange,
}) => {
  const { drbData, isDrbLoading, isSaving, driver, secondDriver, vehicle, trailer } = assignment

  return (
    <>
      {isRouteDateToday && isDrbLoading && !drbData && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
          <Loader2 size={12} className="flex-shrink-0 animate-spin" />
          <span>
            {canAssignVehicles
              ? "Loading driver, vehicle and trailer options..."
              : "Loading driver options..."}
          </span>
        </div>
      )}

      {!(isRouteDateToday && isDrbLoading && !drbData) && (
        <SummaryCard>
          <SummaryRow icon={<Truck size={12} />} label="Vehicle">
            {isRouteDateToday && drbData && canAssignVehicles ? (
              <AssignmentCombobox
                options={drbData.vehicles}
                value={vehicle.id}
                displayValue={vehicle.name || route.vehicleReg || null}
                onSelect={vehicle.onChange}
                placeholder="Select vehicle..."
                searchPlaceholder="Search vehicles..."
                isSaving={isSaving}
                open={openPicker === "vehicle"}
                onOpenChange={onPickerOpenChange("vehicle")}
              />
            ) : (
              <span
                className="block truncate text-sm font-medium text-palette-jungleGreen900"
                title={route.vehicleReg || undefined}
              >
                {route.vehicleReg || (
                  <span className="italic text-palette-jungleGreen400">Unassigned</span>
                )}
              </span>
            )}
          </SummaryRow>

          {/* Checks the list itself: the edge function deploys separately and may send no
              trailers. */}
          {isRouteDateToday && canAssignVehicles && drbData?.trailers && (
            <SummaryRow icon={<Caravan size={12} />} label="Trailer">
              <AssignmentCombobox
                options={drbData.trailers}
                value={trailer.id}
                displayValue={trailer.name}
                onSelect={trailer.onChange}
                placeholder="Select trailer..."
                searchPlaceholder="Search trailers..."
                isSaving={isSaving}
                open={openPicker === "trailer"}
                onOpenChange={onPickerOpenChange("trailer")}
              />
            </SummaryRow>
          )}

          <SummaryRow icon={<User size={12} />} label="Driver">
            {isRouteDateToday && drbData ? (
              <AssignmentCombobox
                options={drbData.drivers}
                value={driver.id}
                displayValue={driver.name || route.driverName || null}
                onSelect={driver.onChange}
                placeholder="Select driver..."
                searchPlaceholder="Search drivers..."
                isSaving={isSaving}
                open={openPicker === "driver"}
                onOpenChange={onPickerOpenChange("driver")}
              />
            ) : (
              <span
                className="block truncate text-sm font-medium text-palette-jungleGreen900"
                title={route.driverName || undefined}
              >
                {route.driverName || (
                  <span className="italic text-palette-jungleGreen400">Unassigned</span>
                )}
              </span>
            )}
          </SummaryRow>

          {route.crewSize > 1 && (
            <SummaryRow icon={<Users size={12} />} label="Second driver">
              {isRouteDateToday && drbData ? (
                <AssignmentCombobox
                  options={drbData.drivers}
                  value={secondDriver.id}
                  displayValue={secondDriver.name || route.secondDriverName || null}
                  onSelect={secondDriver.onChange}
                  placeholder="Select second driver..."
                  searchPlaceholder="Search drivers..."
                  isSaving={isSaving}
                  open={openPicker === "second_driver"}
                  onOpenChange={onPickerOpenChange("second_driver")}
                />
              ) : (
                <span
                  className="block truncate text-sm font-medium text-palette-jungleGreen900"
                  title={route.secondDriverName || undefined}
                >
                  {route.secondDriverName || (
                    <span className="italic text-palette-jungleGreen400">Unassigned</span>
                  )}
                </span>
              )}
            </SummaryRow>
          )}

          {/* Who Sheriff booked versus who is on the route; the name carries the drift colour. */}
          {sheriffDriver && (
            <SummaryRow icon={<UserCheck size={12} />} label="Sheriff">
              {/* One line per booked person: a double-manned route is booked as two shifts. */}
              <div
                className="min-w-0 text-sm"
                title={[sheriffDriver.detail, `Booked in Sheriff. ${COVER_BETA_NOTE}`]
                  .filter(Boolean)
                  .join("\n")}
              >
                {sheriffDriver.booked.map((person) => (
                  <span key={person.key} className="block truncate">
                    {person.name ? (
                      <span
                        className={cn(
                          "font-medium",
                          sheriffDriver.differs
                            ? toneClasses("warning").text
                            : "text-palette-jungleGreen900"
                        )}
                      >
                        {person.name}
                      </span>
                    ) : (
                      <span className="italic text-palette-jungleGreen400">Nobody booked</span>
                    )}
                    {person.window && (
                      <span className="ml-1.5 text-xs tabular-nums text-palette-jungleGreen500">
                        {person.window}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </SummaryRow>
          )}

          <SummaryRow icon={<Clock size={12} />} label="Schedule">
            <span className="text-sm font-medium tabular-nums text-palette-jungleGreen900">
              {format(route.plannedStartTime, "HH:mm")} – {format(route.plannedEndTime, "HH:mm")}
            </span>
          </SummaryRow>

          <SummaryRow icon={<MapPin size={12} />} label="Progress">
            <span className="text-sm font-medium tabular-nums text-palette-jungleGreen900">
              {route.completedStops} / {route.totalStops} stops
            </span>
          </SummaryRow>

          {route.samsaraRouteId && (
            <SummaryRow icon={<ExternalLink size={12} />} label="Samsara">
              <a
                href={getSamsaraRouteUrl(route.samsaraRouteId)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-palette-jungleGreen900 underline decoration-palette-jungleGreen300 underline-offset-2 hover:decoration-palette-jungleGreen900"
              >
                Open route
              </a>
            </SummaryRow>
          )}
        </SummaryCard>
      )}

      {isRouteDateToday && drbData && !drbData.currentAssignment && (
        <div className="rounded-lg border border-palette-jungleGreen200 bg-palette-jungleGreen000 p-3">
          <div className="flex items-center gap-2 text-xs text-palette-jungleGreen500">
            <AlertTriangle size={12} />
            <span>No DRB record found for route {route.code}. Assignments cannot be edited.</span>
          </div>
        </div>
      )}
    </>
  )
}

export default RouteAssignmentSummary
